/*
 * <gg-spa-menu> — spa treatments menu + info-panel toggle (epic spm0).
 *
 * Topology: a VERTICAL category column with the treatment sublist beside it in
 * normal flow, and selection is PERSISTENT (--active). This is the Shop nav's
 * *mobile* --active path — no dead-zone to bridge — so NONE of the Shop hover
 * engine's apparatus (mask-wipe hit-testing, WeakMap pending-close, timers,
 * animationend) is needed. One delegated click listener + an optional desktop
 * pointerover preview. Reveal is pure CSS keyed off --active.
 *
 * Contract:
 *   JS hooks:  .gg-spa-cat / .gg-spa-treat / .gg-spa-treatlist / .gg-spa-panel
 *   identity:  data-cat-id (slug: type-N / brand-N) + data-treat-id
 *   CSS state: --active on cat / treat / treatlist / panel
 *   SSR:       default category + its first treatment/panel already carry --active
 */
class GgSpaMenu extends HTMLElement {
  connectedCallback() {
    // Click-only: hovering must NOT change the menu (avoids accidentally
    // switching category while moving the cursor toward the sublist).
    this.addEventListener('click', this.#onClick);
  }

  #onClick = (e) => {
    const cat = e.target.closest('.gg-spa-cat');
    if (cat && this.contains(cat)) {
      this.#selectCategory(cat.dataset.catId);
      return;
    }
    const treat = e.target.closest('.gg-spa-treat');
    if (treat && this.contains(treat)) {
      this.#selectTreatment(treat.dataset.treatId);
    }
  };

  // Click a category: expand its sublist (accordion — only one open) + auto-select
  // its first treatment.
  #selectCategory(catId) {
    this.querySelectorAll('.gg-spa-treatlist').forEach((list) => {
      list.classList.toggle('gg-spa-treatlist--active', list.dataset.catId === catId);
    });
    this.querySelectorAll('.gg-spa-cat').forEach((btn) => {
      const on = btn.dataset.catId === catId;
      btn.classList.toggle('gg-spa-cat--active', on);
      btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    const list = this.querySelector(`.gg-spa-treatlist[data-cat-id="${catId}"]`);
    const first = list && list.querySelector('.gg-spa-treat');
    if (first) this.#selectTreatment(first.dataset.treatId);
  }

  // Click a treatment: mark it + its panel active; clear the rest.
  #selectTreatment(treatId) {
    this.querySelectorAll('.gg-spa-treat').forEach((btn) => {
      btn.classList.toggle('gg-spa-treat--active', btn.dataset.treatId === treatId);
    });
    this.querySelectorAll('.gg-spa-panel').forEach((panel) => {
      const on = panel.dataset.treatId === treatId;
      panel.classList.toggle('gg-spa-panel--active', on);
      if (on) this.#replayReveal(panel);
    });
  }

  // Restart the fade-in reveal on the shown panel. Toggling display via a class
  // doesn't reliably replay a CSS animation, so we remove the animating class,
  // force a reflow, then re-add it — the standard restart trick.
  #replayReveal(panel) {
    panel.classList.remove('gg-spa-panel--reveal');
    // eslint-disable-next-line no-unused-expressions
    void panel.offsetWidth; // force reflow so the removal takes effect
    panel.classList.add('gg-spa-panel--reveal');
  }
}

if (!customElements.get('gg-spa-menu')) {
  customElements.define('gg-spa-menu', GgSpaMenu);
}
